import { API_BASE } from "./config/api";
import React, { useEffect, useState } from "react";
import {
  Input,
  Button,
  Card,
  Typography,
  Alert,
  Modal,
} from "antd";
import {
  TeamOutlined,
  UserOutlined,
  SafetyOutlined,
  UnlockOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { clearClientSession, clearDistributorSession } from "./config/auth";
import {
  enableBiometricLogin,
  getBiometricCapability,
  getBiometricAccountHint,
  hasBiometricLogin,
  isBiometricCancellation,
  isNativeBiometricRealm,
  readBiometricLogin,
} from "./config/biometricAuth";

const { Title, Text } = Typography;




/* =========================================================
   ADMIN FLAG HELPER
========================================================= */

function extractIsAdminFlag(payload) {
  const cand =
    payload?.is_admin ??
    payload?.isadmin ??
    payload?.admin ??
    payload?.data?.is_admin ??
    payload?.data?.isadmin ??
    payload?.data?.admin ??
    payload?.user?.is_admin ??
    payload?.user?.isadmin ??
    payload?.user?.admin;

  if (cand === undefined || cand === null) {
    return false;
  }

  const s = String(cand)
    .trim()
    .toLowerCase();

  return (
    s === "true" ||
    s === "1" ||
    s === "yes" ||
    s === "y"
  );
}


/* =========================================================
   NAME FORMATTER
========================================================= */

function toSentenceCase(str = "") {
  return String(str)
    .toLowerCase()
    .split(" ")
    .filter(Boolean)
    .map(
      (w) =>
        w.charAt(0).toUpperCase() +
        w.slice(1)
    )
    .join(" ");
}


/* =========================================================
   MAIN COMPONENT
========================================================= */

export default function DistributorLogin() {

  const isMfdApp =
    process.env.REACT_APP_APP_VARIANT === "mfd";

/* ================= MFD STATES ================= */

  const [arnNumber, setArnNumber] =
    useState("");

  const [password, setPassword] =
    useState("");

  const [loading, setLoading] =
    useState(false);

  const [loginError, setLoginError] =
    useState("");

  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricEnabled, setBiometricEnabled] = useState(false);
  const [biometricLoading, setBiometricLoading] = useState(false);
  const [biometricOffer, setBiometricOffer] = useState(null);
  const [showCredentialLogin, setShowCredentialLogin] = useState(true);

  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotStep, setForgotStep] = useState("request");
  const [forgotArn, setForgotArn] = useState("");
  const [forgotOtp, setForgotOtp] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMessage, setForgotMessage] = useState("");
  const [forgotError, setForgotError] = useState("");


  useEffect(() => {
    let alive = true;
    (async () => {
      if (!isNativeBiometricRealm("mfd")) return;
      const capability = await getBiometricCapability("mfd");
      const enabled = capability.available ? await hasBiometricLogin("mfd") : false;
      if (alive) {
        setBiometricAvailable(capability.available);
        setBiometricEnabled(enabled);
        setShowCredentialLogin(!enabled);
        if (enabled) {
          window.setTimeout(() => handleBiometricLogin(true), 150);
        }
      }
    })();
    return () => { alive = false; };
  }, []);

  const finishDistributorLogin = (authToken, fullArn, isAdmin, holderName = "") => {
    clearClientSession();
    clearDistributorSession();
    sessionStorage.setItem("distributor_auth_token", authToken);
    localStorage.setItem("arn_number", fullArn);
    localStorage.setItem("is_admin", String(isAdmin));
    if (holderName) localStorage.setItem("arn_holder_name", toSentenceCase(holderName));
    else localStorage.removeItem("arn_holder_name");
    window.location.href = "/dashboard";
  };

  const handleBiometricLogin = async (autoStart = false) => {
    setLoginError("");
    setBiometricLoading(true);
    try {
      const saved = await readBiometricLogin("mfd");
      if (!saved?.deviceCredential) {
        setBiometricEnabled(false);
        setLoginError("Biometric login is not configured. Please sign in with ARN and password.");
        return;
      }

      const res = await axios.post(
        `${API_BASE}/distributor_biometric_exchange.php`,
        {},
        {
          headers: {
            "X-KG-Biometric-Authorization": `Bearer ${saved.deviceCredential}`,
          },
        }
      );

      const authToken = res.data?.auth_token ?? res.data?.token;
      if (res.data?.status !== "ok" || !authToken) {
        throw new Error("Biometric session exchange failed.");
      }

      finishDistributorLogin(
        authToken,
        res.data?.arn_number || saved.userId || "",
        extractIsAdminFlag(res.data),
        res.data?.arn_holder_name || saved.displayName || ""
      );
    } catch (error) {
      if (isBiometricCancellation(error)) {
        if (autoStart) setShowCredentialLogin(true);
      } else {
        setShowCredentialLogin(true);
        setLoginError("Biometric login could not be completed. Please use ARN and password.");
      }
    } finally {
      setBiometricLoading(false);
    }
  };

  const acceptBiometricOffer = async () => {
    if (!biometricOffer) return;
    setBiometricLoading(true);
    try {
      const enrollRes = await axios.post(
        `${API_BASE}/distributor_biometric_enroll.php`,
        { device_label: "MFSetu - MFD" },
        {
          headers: {
            "X-KG-Distributor-Authorization": `Bearer ${biometricOffer.token}`,
          },
        }
      );
      const deviceCredential = enrollRes.data?.device_credential;
      if (enrollRes.data?.status !== "ok" || !deviceCredential) {
        throw new Error("Biometric enrollment failed.");
      }

      await enableBiometricLogin("mfd", {
        deviceCredential,
        userId: biometricOffer.userId,
        displayName: biometricOffer.displayName,
        isAdmin: biometricOffer.isAdmin,
      });
      setBiometricEnabled(true);
      finishDistributorLogin(
        biometricOffer.token, biometricOffer.userId, biometricOffer.isAdmin, biometricOffer.displayName
      );
    } catch (error) {
      if (!isBiometricCancellation(error)) {
        setLoginError("Biometric setup could not be completed. You can enable it later from the menu.");
      }
      finishDistributorLogin(
        biometricOffer.token, biometricOffer.userId, biometricOffer.isAdmin, biometricOffer.displayName
      );
    } finally {
      setBiometricLoading(false);
      setBiometricOffer(null);
    }
  };


  /* =========================================================
     MFD LOGIN
  ========================================================= */

  const handleLogin = async () => {

    setLoginError("");

    if (!arnNumber || !password) {
      setLoginError(
        "Please enter both ARN number and password."
      );
      return;
    }

    setLoading(true);

    const fullArn =
      "ARN-" + arnNumber;

    try {

      const res = await axios.post(
        `${API_BASE}/distributor_login.php`,
        {
          arn_number: fullArn,
          password: password,
        }
      );


      if (res.data?.status === "ok") {

        const authToken = res.data?.auth_token ?? res.data?.token;

        if (!authToken) {
          setLoginError(
            "Login succeeded but authentication token was not received."
          );
          return;
        }

        const isAdmin =
          extractIsAdminFlag(res.data);


        const holderName = res.data?.arn_holder_name || "";

        if (biometricAvailable && (!biometricEnabled || getBiometricAccountHint("mfd") !== fullArn)) {
          setBiometricOffer({
            token: authToken,
            userId: fullArn,
            displayName: holderName,
            isAdmin,
          });
          return;
        }

        finishDistributorLogin(authToken, fullArn, isAdmin, holderName);

      } else {
        localStorage.removeItem("arn_holder_name");
        setLoginError(
          res.data?.message || "Wrong ARN number or password."
        );
      }

    } catch (err) {

      // Do not dump Axios request/config here because it may contain credentials.
      setLoginError(
        "Unable to login. Please try again."
      );

    } finally {

      setLoading(false);

    }
  };



  const closeForgot = () => {
    setForgotOpen(false);
    setForgotStep("request");
    setForgotArn("");
    setForgotOtp("");
    setResetToken("");
    setNewPassword("");
    setConfirmPassword("");
    setForgotMessage("");
    setForgotError("");
  };

  const requestResetOtp = async () => {
    setForgotError("");
    setForgotMessage("");
    const digits = forgotArn.replace(/\D/g, "");
    if (!digits) return setForgotError("Please enter your ARN number.");
    setForgotLoading(true);
    try {
      await axios.post(`${API_BASE}/distributor_forgot_password_request.php`, {
        arn_number: `ARN-${digits}`,
      });
      setForgotMessage("If this ARN has a registered email, an OTP has been sent to it.");
      setForgotStep("verify");
    } catch (e) {
      if (e?.response?.status === 429) setForgotError("Too many requests. Please try again later.");
      else setForgotError("Unable to process the request right now.");
    } finally {
      setForgotLoading(false);
    }
  };

  const verifyResetOtp = async () => {
    setForgotError("");
    if (!/^\d{6}$/.test(forgotOtp)) return setForgotError("Enter the 6 digit OTP.");
    setForgotLoading(true);
    try {
      const res = await axios.post(`${API_BASE}/distributor_forgot_password_verify.php`, {
        arn_number: `ARN-${forgotArn.replace(/\D/g, "")}`,
        otp: forgotOtp,
      });
      if (!res.data?.reset_token) throw new Error("No reset token");
      setResetToken(res.data.reset_token);
      setForgotStep("reset");
      setForgotMessage("Email OTP verified. Set a new login password.");
    } catch (e) {
      if (e?.response?.status === 429) setForgotError("Too many attempts. Please request a new OTP later.");
      else setForgotError("OTP is invalid or has expired.");
    } finally {
      setForgotLoading(false);
    }
  };

  const resetForgotPassword = async () => {
    setForgotError("");
    if (newPassword.length < 10) return setForgotError("Password must be at least 10 characters.");
    if (newPassword !== confirmPassword) return setForgotError("Passwords do not match.");
    setForgotLoading(true);
    try {
      await axios.post(`${API_BASE}/distributor_forgot_password_reset.php`, {
        reset_token: resetToken,
        new_password: newPassword,
      });
      closeForgot();
      setLoginError("");
      alert("Password changed successfully. Please sign in with your new password.");
    } catch (e) {
      setForgotError("Reset link has expired or the password could not be changed.");
    } finally {
      setForgotLoading(false);
    }
  };

  /* =========================================================
     MFD LOGIN CONTENT
  ========================================================= */

  const mfdLoginContent = (

    <div
      style={{
        maxWidth: 520,
        margin: "0 auto",
      }}
    >

      <div
        style={{
          marginBottom: 22,
        }}
      >

        <Title
          level={4}
          style={{
            marginBottom: 4,
          }}
        >
          <TeamOutlined /> MFD Sign In
        </Title>

        <Text type="secondary">
          Sign in to manage your clients,
          portfolios and reports.
        </Text>

      </div>


      {loginError && (

        <Alert
          type="error"
          showIcon
          message={loginError}
          style={{
            marginBottom: 18,
          }}
        />

      )}


      {biometricAvailable && biometricEnabled && !showCredentialLogin ? (
        <div style={{ textAlign: "center" }}>
          <UnlockOutlined style={{ fontSize: 42, marginBottom: 12 }} />
          <Title level={4} style={{ marginBottom: 6 }}>Biometric Login</Title>
          <Text type="secondary">
            Authenticate with your device biometrics to securely sign in.
          </Text>
          <Button
            type="link"
            block
            disabled={biometricLoading}
            onClick={() => setShowCredentialLogin(true)}
            style={{ marginTop: 14 }}
          >
            Cancel
          </Button>
        </div>
      ) : (
        <>
          {/* ARN NUMBER */}
          <div style={{ marginBottom: 18 }}>
            <label style={{ display: "block", marginBottom: 6, fontWeight: 500 }}>
              ARN Number
            </label>
            <Input
              addonBefore="ARN-"
              size="large"
              type="text"
              inputMode="numeric"
              value={arnNumber}
              placeholder="Enter ARN Number"
              onChange={(e) =>
                setArnNumber(e.target.value.replace(/\D/g, "").slice(0, 8))
              }
              onPressEnter={handleLogin}
            />
          </div>

          {/* PASSWORD */}
          <div style={{ marginBottom: 18 }}>
            <label style={{ display: "block", marginBottom: 6, fontWeight: 500 }}>
              Password
            </label>
            <Input.Password
              size="large"
              value={password}
              placeholder="Enter Password"
              onChange={(e) => setPassword(e.target.value)}
              visibilityToggle={true}
              autoComplete="current-password"
              onPressEnter={handleLogin}
            />
          </div>

          <Button
            type="primary"
            size="large"
            block
            loading={loading}
            onClick={handleLogin}
          >
            Sign In
          </Button>

          {biometricAvailable && biometricEnabled && (
            <Button
              size="large"
              block
              icon={<UnlockOutlined />}
              loading={biometricLoading}
              onClick={() => handleBiometricLogin(false)}
              style={{ marginTop: 10 }}
            >
              Login with Biometrics
            </Button>
          )}

          <Button
            type="link"
            block
            style={{ marginTop: 10 }}
            onClick={() => setForgotOpen(true)}
          >
            Forgot Password?
          </Button>
        </>
      )}


      {/* SECURITY MESSAGE */}

      <Alert
        type="info"
        showIcon
        icon={<SafetyOutlined />}
        message="Secure Access"
description={
  isMfdApp
    ? "Your MFSetu account and portfolio information are protected."
    : "Your KGOnline account and portfolio information are protected."
}
        style={{
          marginTop: 18,
        }}
      />

    </div>
  );


  /* =========================================================
     MAIN UI
  ========================================================= */

  return (

    <div
      style={{
        minHeight: "100vh",

        background:
          "linear-gradient(135deg, #f7faff 0%, #eef5ff 100%)",

        padding: "50px 18px",

        boxSizing: "border-box",
      }}
    >

      <div
        style={{
          width: "100%",
          maxWidth: 1000,
          margin: "0 auto",
        }}
      >


        {/* ================= HEADER ================= */}

        <div
          style={{
            textAlign: "center",
            marginBottom: 28,
          }}
        >

<Title
  level={2}
  style={{
    marginBottom: 5,
  }}
>
  {isMfdApp
    ? "Welcome to MFSetu"
    : "Welcome to KGOnline"}
</Title>

<Text
  type="secondary"
  style={{
    fontSize: 15,
  }}
>
  {isMfdApp
    ? "Track Your Client’s Portfolio"
    : "Distributor login for Client's Portfolio and Report Management"}
</Text>

        </div>


        {/* ================= MAIN CARD ================= */}

        <Card
          bordered={false}
          style={{
            borderRadius: 14,

            boxShadow:
              "0 8px 30px rgba(0,0,0,0.07)",
          }}
          bodyStyle={{
            padding: 28,
          }}
        >


          {/* =================================================
              MFD / CUSTOMER TABS
          ================================================= */}

          {mfdLoginContent}

        </Card>

      </div>


        <Modal
          title="Enable Biometric Login?"
          open={!!biometricOffer}
          closable={false}
          maskClosable={false}
          keyboard={false}
          footer={[
            <Button key="later" disabled={biometricLoading} onClick={() => {
              const offer = biometricOffer;
              setBiometricOffer(null);
              if (offer) finishDistributorLogin(offer.token, offer.userId, offer.isAdmin, offer.displayName);
            }}>
              Not Now
            </Button>,
            <Button key="enable" type="primary" loading={biometricLoading} onClick={acceptBiometricOffer}>
              Enable
            </Button>,
          ]}
        >
          <Text>Use your device biometrics for faster secure sign-in on this mobile device. Your login password will not be stored.</Text>
        </Modal>

        <Modal
          title="Forgot Login Password"
          open={forgotOpen}
          onCancel={closeForgot}
          footer={null}
          destroyOnClose
          maskClosable={false}
        >
          {forgotMessage && <Alert type="success" showIcon message={forgotMessage} style={{ marginBottom: 14 }} />}
          {forgotError && <Alert type="error" showIcon message={forgotError} style={{ marginBottom: 14 }} />}

          {forgotStep === "request" && (
            <>
<Text type="secondary">
  {isMfdApp
    ? "OTP will be sent only to your registered email address."
    : "OTP will be sent only to the registered email address in KGOnline."}
</Text>
              <Input
                addonBefore="ARN-"
                size="large"
                inputMode="numeric"
                value={forgotArn}
                placeholder="Enter ARN Number"
                onChange={(e) => setForgotArn(e.target.value.replace(/\D/g, "").slice(0, 12))}
                style={{ marginTop: 14 }}
              />
              <Button type="primary" block size="large" loading={forgotLoading} onClick={requestResetOtp} style={{ marginTop: 14 }}>
                Send Email OTP
              </Button>
            </>
          )}

          {forgotStep === "verify" && (
            <>
              <Input
                size="large"
                inputMode="numeric"
                maxLength={6}
                value={forgotOtp}
                placeholder="6 digit OTP"
                onChange={(e) => setForgotOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              />
              <Button type="primary" block size="large" loading={forgotLoading} onClick={verifyResetOtp} style={{ marginTop: 14 }}>
                Verify OTP
              </Button>
            </>
          )}

          {forgotStep === "reset" && (
            <>
              <Input.Password
                size="large"
                visibilityToggle={true}
                autoComplete="new-password"
                value={newPassword}
                placeholder="New Password"
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <Input.Password
                size="large"
                visibilityToggle={true}
                autoComplete="new-password"
                value={confirmPassword}
                placeholder="Confirm New Password"
                onChange={(e) => setConfirmPassword(e.target.value)}
                style={{ marginTop: 12 }}
              />
              <Button type="primary" block size="large" loading={forgotLoading} onClick={resetForgotPassword} style={{ marginTop: 14 }}>
                Set New Password
              </Button>
            </>
          )}
        </Modal>
    </div>
  );
}
